import React, { useState } from "react";
import {
  Landmark,
  Building2,
  BookOpen,
  BarChart3,
  FileSpreadsheet,
  Plus,
  Users,
  TrendingUp,
  Receipt,
  FileStack,
  Wallet,
} from "lucide-react";
import { TrustAccountsContent } from "../trust/TrustPage";
import { ChartOfAccountsContent } from "../trust/ChartOfAccountsPage";
import OperatingOverviewTab from "./OperatingOverviewTab";
import GeneralLedgerTab from "./GeneralLedgerTab";
import TransactionsTab from "./TransactionsTab";
import AccountsReceivableTab from "./AccountsReceivableTab";
import AccountsPayableTab from "./AccountsPayableTab";
import ExpensesTab from "./ExpensesTab";
import AddBankAccountModal from "./AddBankAccountModal";
import ClientBalancesTab from "./ClientBalancesTab";
import FinancialStatementsTab from "./FinancialStatementsTab";

type TopTab = "trust" | "operating" | "client-balances" | "financial-statements" | "chart-of-accounts";
type OperatingSubTab = "overview" | "general-ledger" | "transactions" | "accounts-receivable" | "accounts-payable" | "expenses";

const operatingSubTabs = [
  { key: "overview" as const, label: "Overview", Icon: Building2 },
  { key: "general-ledger" as const, label: "General Ledger", Icon: BookOpen },
  { key: "transactions" as const, label: "Transactions", Icon: FileSpreadsheet },
  { key: "accounts-receivable" as const, label: "Accounts Receivable", Icon: Receipt },
  { key: "accounts-payable" as const, label: "Accounts Payable", Icon: FileStack },
  { key: "expenses" as const, label: "Expenses", Icon: Wallet },
];

const AccountingPage: React.FC = () => {
  const [topTab, setTopTab] = useState<TopTab>("operating");
  const [operatingSubTab, setOperatingSubTab] = useState<OperatingSubTab>("overview");
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleAccountCreated = () => {
    setRefreshKey((k) => k + 1);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F4F6F8" }}>
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800">Accounting</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage trust funds, operating accounts, and general ledger
          </p>
        </div>

        {/* Top-level pill tabs */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1">
          <button
            onClick={() => setTopTab("operating")}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-colors ${
              topTab === "operating"
                ? "bg-brand text-white"
                : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
            }`}
          >
            <Building2 className="h-4 w-4" />
            Operating Accounts
          </button>
          <button
            onClick={() => setTopTab("trust")}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-colors ${
              topTab === "trust"
                ? "bg-brand text-white"
                : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
            }`}
          >
            <Landmark className="h-4 w-4" />
            Trust Accounts
          </button>
          <button
            onClick={() => setTopTab("client-balances")}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-colors ${
              topTab === "client-balances"
                ? "bg-brand text-white"
                : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
            }`}
          >
            <Users className="h-4 w-4" />
            Client Balances
          </button>
          <button
            onClick={() => setTopTab("financial-statements")}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-colors ${
              topTab === "financial-statements"
                ? "bg-brand text-white"
                : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
            }`}
          >
            <TrendingUp className="h-4 w-4" />
            Financial Statements
          </button>
          <button
            onClick={() => setTopTab("chart-of-accounts")}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-colors ${
              topTab === "chart-of-accounts"
                ? "bg-brand text-white"
                : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            Chart of Accounts
          </button>
          <button
            onClick={() => setShowAddAccount(true)}
            className="ml-auto inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Bank Account
          </button>
        </div>

        {/* Trust Tab */}
        {topTab === "trust" && <TrustAccountsContent key={`trust-${refreshKey}`} />}

        {/* Client Balances Tab */}
        {topTab === "client-balances" && <ClientBalancesTab />}

        {/* Financial Statements Tab */}
        {topTab === "financial-statements" && (
          <FinancialStatementsTab key={`fs-${refreshKey}`} />
        )}

        {/* Chart of Accounts Tab */}
        {topTab === "chart-of-accounts" && <ChartOfAccountsContent />}

        {/* Operating Tab */}
        {topTab === "operating" && (
          <>
            {/* Operating sub-tabs */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
              <div className="flex overflow-x-auto border-b border-slate-200">
                {operatingSubTabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setOperatingSubTab(tab.key)}
                    className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      operatingSubTab === tab.key
                        ? "border-brand text-brand"
                        : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                    }`}
                  >
                    <tab.Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Operating sub-tab content */}
            {operatingSubTab === "overview" && (
              <OperatingOverviewTab key={`op-overview-${refreshKey}`} />
            )}
            {operatingSubTab === "general-ledger" && <GeneralLedgerTab />}
            {operatingSubTab === "transactions" && <TransactionsTab />}
            {operatingSubTab === "accounts-receivable" && <AccountsReceivableTab />}
            {operatingSubTab === "accounts-payable" && <AccountsPayableTab />}
            {operatingSubTab === "expenses" && <ExpensesTab />}
          </>
        )}
      </div>

      {/* Add Account Modal */}
      <AddBankAccountModal
        open={showAddAccount}
        onClose={() => setShowAddAccount(false)}
        onCreated={handleAccountCreated}
        defaultKind={topTab === "trust" ? "trust" : "operating"}
      />
    </div>
  );
};

export default AccountingPage;
